import type { IntegerAmount } from '@actual-app/core/shared/util';

import { faltante, opcionSuficiente } from './objetivos';
import type { DatosCategoriaMes, ImporteCategoria } from './objetivos';

/**
 * Borrador de «Asignar el mes» (6-oct): lo que se asigna en esa pantalla no
 * va al presupuesto al momento; se acumula aquí hasta «Guardar presupuesto».
 * Funciones puras, sin React.
 */
export type CambioBorrador = {
  /** Nuevo asignado del mes (céntimos); sin él, se queda como está. */
  asignado?: IntegerAmount;
  /** «Suficiente»: al guardar, marca la categoría «suficiente» este mes. */
  suficiente?: boolean;
};

/** Cambios por id de categoría. */
export type Borrador = Readonly<Record<string, CambioBorrador>>;

export const BORRADOR_VACIO: Borrador = {};

const SIN_DATOS: DatosCategoriaMes = {
  goal: null,
  longGoal: false,
  budgeted: 0,
  balance: 0,
  spent: 0,
};

function limpiar(cambio: CambioBorrador): CambioBorrador | null {
  const limpio: CambioBorrador = {};
  if (cambio.asignado != null) {
    limpio.asignado = cambio.asignado;
  }
  if (cambio.suficiente) {
    limpio.suficiente = true;
  }
  return limpio.asignado == null && !limpio.suficiente ? null : limpio;
}

function conCambio(
  borrador: Borrador,
  id: string,
  cambio: CambioBorrador,
): Borrador {
  const { [id]: _anterior, ...resto } = borrador;
  const limpio = limpiar(cambio);
  return limpio ? { ...resto, [id]: limpio } : resto;
}

/**
 * Pone `importe` como asignado de la categoría en el borrador. Si coincide
 * con lo guardado (`original`), deja de ser un cambio.
 */
export function asignarEnBorrador(
  borrador: Borrador,
  id: string,
  importe: IntegerAmount,
  original: IntegerAmount,
): Borrador {
  const anterior = borrador[id] ?? {};
  return conCambio(borrador, id, {
    ...anterior,
    asignado: importe === original ? undefined : importe,
  });
}

/** Aplica varios importes a la vez (auto-asignar, «Más opciones…»). */
export function asignarVariosEnBorrador(
  borrador: Borrador,
  importes: readonly ImporteCategoria[],
  originales: ReadonlyMap<string, DatosCategoriaMes>,
): Borrador {
  return importes.reduce(
    (b, { category, amount }) =>
      asignarEnBorrador(
        b,
        category,
        amount,
        originales.get(category)?.budgeted ?? 0,
      ),
    borrador,
  );
}

/** Marca (o desmarca) la categoría como «Suficiente» este mes. */
export function marcarSuficiente(
  borrador: Borrador,
  id: string,
  suficiente: boolean,
): Borrador {
  return conCambio(borrador, id, { ...(borrador[id] ?? {}), suficiente });
}

/** Asignado que enseña la fila: el del borrador o el guardado. */
export function asignadoConBorrador(
  borrador: Borrador,
  id: string,
  original: IntegerAmount,
): IntegerAmount {
  return borrador[id]?.asignado ?? original;
}

/**
 * Datos de la categoría como quedarían al guardar: el asignado nuevo, el
 * saldo movido en la misma diferencia y, si es «Suficiente», ignorada.
 */
export function datosConBorrador(
  datos: DatosCategoriaMes | undefined,
  cambio: CambioBorrador | undefined,
): DatosCategoriaMes {
  const base = datos ?? SIN_DATOS;
  if (!cambio) {
    return base;
  }
  const asignado = cambio.asignado ?? base.budgeted;
  const diferencia = asignado - base.budgeted;
  return {
    ...base,
    budgeted: asignado,
    balance: base.balance + diferencia,
    ignorada: cambio.suficiente ? true : base.ignorada,
  };
}

/** Mapa de datos con todo el borrador aplicado. */
export function mapaConBorrador(
  datos: ReadonlyMap<string, DatosCategoriaMes>,
  borrador: Borrador,
): Map<string, DatosCategoriaMes> {
  const mapa = new Map(datos);
  for (const [id, cambio] of Object.entries(borrador)) {
    mapa.set(id, datosConBorrador(datos.get(id), cambio));
  }
  return mapa;
}

/** Lo que el borrador saca de «Listo para asignar» (suma de diferencias). */
export function diferenciaBorrador(
  borrador: Borrador,
  datos: ReadonlyMap<string, DatosCategoriaMes>,
): IntegerAmount {
  let total = 0;
  for (const [id, cambio] of Object.entries(borrador)) {
    if (cambio.asignado != null) {
      total += cambio.asignado - (datos.get(id)?.budgeted ?? 0);
    }
  }
  return total;
}

/** «Listo para asignar» como quedaría al guardar el borrador. */
export function listosConBorrador(
  toBudget: IntegerAmount,
  borrador: Borrador,
  datos: ReadonlyMap<string, DatosCategoriaMes>,
): IntegerAmount {
  return toBudget - diferenciaBorrador(borrador, datos);
}

/**
 * Cambios reales: categorías cuyo asignado difiere del guardado o que se
 * marcan «Suficiente» sin estar ya pausadas.
 */
export function cambiosEfectivos(
  borrador: Borrador,
  datos: ReadonlyMap<string, DatosCategoriaMes>,
): string[] {
  return Object.entries(borrador)
    .filter(([id, cambio]) => {
      const original = datos.get(id);
      const asignado =
        cambio.asignado != null &&
        cambio.asignado !== (original?.budgeted ?? 0);
      const suficiente = !!cambio.suficiente && !original?.ignorada;
      return asignado || suficiente;
    })
    .map(([id]) => id);
}

export function contarCambios(
  borrador: Borrador,
  datos: ReadonlyMap<string, DatosCategoriaMes>,
): number {
  return cambiosEfectivos(borrador, datos).length;
}

export type AccionGuardar = {
  /** Para una sola acción `budget-amounts`. */
  amounts: ImporteCategoria[];
  /** Categorías que se pausan este mes (marca de nota de mes). */
  suficientes: string[];
  /** Los importes de antes, para «Deshacer». */
  anteriores: ImporteCategoria[];
};

/** Lo que hace «Guardar presupuesto» con el borrador. */
export function accionGuardar(
  borrador: Borrador,
  datos: ReadonlyMap<string, DatosCategoriaMes>,
): AccionGuardar {
  const amounts: ImporteCategoria[] = [];
  const anteriores: ImporteCategoria[] = [];
  const suficientes: string[] = [];
  for (const id of cambiosEfectivos(borrador, datos)) {
    const cambio = borrador[id];
    const original = datos.get(id);
    const antes = original?.budgeted ?? 0;
    if (cambio.asignado != null && cambio.asignado !== antes) {
      amounts.push({ category: id, amount: cambio.asignado });
      anteriores.push({ category: id, amount: antes });
    }
    if (cambio.suficiente && !original?.ignorada) {
      suficientes.push(id);
    }
  }
  return { amounts, suficientes, anteriores };
}

// ---------------------------------------------------------------------------
// sessionStorage: el borrador sobrevive a un refresco
// ---------------------------------------------------------------------------

export function claveBorrador(month: string): string {
  return `rumbo.asignar.borrador.${month}`;
}

/** Lee un borrador guardado; ante cualquier cosa rara, vacío. */
export function leerBorrador(raw: string | null | undefined): Borrador {
  if (!raw) {
    return BORRADOR_VACIO;
  }
  try {
    const valor: unknown = JSON.parse(raw);
    if (!valor || typeof valor !== 'object' || Array.isArray(valor)) {
      return BORRADOR_VACIO;
    }
    let borrador: Borrador = BORRADOR_VACIO;
    for (const [id, cambio] of Object.entries(valor)) {
      if (!cambio || typeof cambio !== 'object') {
        continue;
      }
      const { asignado, suficiente } = cambio as Record<string, unknown>;
      borrador = conCambio(borrador, id, {
        asignado:
          typeof asignado === 'number' && Number.isInteger(asignado)
            ? asignado
            : undefined,
        suficiente: suficiente === true,
      });
    }
    return borrador;
  } catch {
    return BORRADOR_VACIO;
  }
}

// ---------------------------------------------------------------------------
// Botón contextual del teclado en modo borrador
// ---------------------------------------------------------------------------

export type BotonesBorrador = {
  /** «Asignar X €»: deja el asignado en `importe` (absoluto). */
  asignar: { importe: IntegerAmount; motivo: AsignarMotivo } | null;
  /** «Suficiente este mes». */
  suficiente: boolean;
};

export type AsignarMotivo = 'saldo-negativo' | 'objetivo' | 'mes-pasado';

/**
 * Botones verdes del teclado en «Asignar el mes» (modo borrador), con los
 * datos ya con el borrador aplicado:
 * - saldo negativo → asignar lo que cubre el rojo;
 * - con objetivo y le falta → asignar lo que falta;
 * - sin objetivo, nada asignado y algo el mes pasado → repetirlo;
 * - «Suficiente» solo si tiene objetivo y le falta dinero este mes
 *   (`opcionSuficiente`), o si ya está marcada en el borrador (para quitarla).
 */
export function botonesBorrador({
  datos,
  asignadoMesPasado,
  marcadaSuficiente,
}: {
  datos: DatosCategoriaMes;
  asignadoMesPasado: IntegerAmount | null;
  marcadaSuficiente: boolean;
}): BotonesBorrador {
  let asignar: BotonesBorrador['asignar'] = null;
  if (datos.balance < 0) {
    asignar = {
      importe: datos.budgeted - datos.balance,
      motivo: 'saldo-negativo',
    };
  } else if (datos.goal != null) {
    const falta = faltante(datos);
    if (falta > 0) {
      asignar = { importe: datos.budgeted + falta, motivo: 'objetivo' };
    }
  } else if (
    datos.budgeted === 0 &&
    asignadoMesPasado != null &&
    asignadoMesPasado > 0
  ) {
    asignar = { importe: asignadoMesPasado, motivo: 'mes-pasado' };
  }
  // Regla «Suficiente» (= snooze de YNAB): solo si al objetivo le falta
  // dinero este mes (o ya está marcada en el borrador, para quitarla).
  const suficiente = marcadaSuficiente || opcionSuficiente(datos) === 'poner';
  return { asignar, suficiente };
}

export type PestanaAsignar =
  | 'con-objetivo'
  | 'sin-objetivo'
  | 'pagos-fijos'
  | 'todas';

export const PESTANAS_ASIGNAR: readonly PestanaAsignar[] = [
  'con-objetivo',
  'sin-objetivo',
  'pagos-fijos',
  'todas',
];

export const CLAVE_PESTANA = 'rumbo.asignar.pestana';

export function leerPestana(raw: string | null | undefined): PestanaAsignar {
  return PESTANAS_ASIGNAR.includes(raw as PestanaAsignar)
    ? (raw as PestanaAsignar)
    : 'todas';
}

/** Filtra los grupos por pestaña y quita los que se quedan vacíos. */
export function filtrarPorPestana<
  G extends { categories: readonly { id: string }[] },
>(
  grupos: readonly G[],
  pestana: PestanaAsignar,
  datos: ReadonlyMap<string, DatosCategoriaMes>,
  conPagoFijo: ReadonlySet<string>,
): G[] {
  if (pestana === 'todas') {
    return [...grupos];
  }
  const entra = (id: string) => {
    switch (pestana) {
      case 'con-objetivo':
        return datos.get(id)?.goal != null;
      case 'sin-objetivo':
        return datos.get(id)?.goal == null;
      case 'pagos-fijos':
        return conPagoFijo.has(id);
      default:
        return true;
    }
  };
  return grupos
    .map(g => ({ ...g, categories: g.categories.filter(c => entra(c.id)) }))
    .filter(g => g.categories.length > 0);
}
